import React, { useCallback, useState } from 'react';

import { Modal, Spin, Button, Form, Input } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { BlueButton } from '@share/components';
import { DownLoadShareType, HandleDownload, HandleShare } from '@share/api';
import { FunctionalRootState } from '@share/utils';

import { ModalConfirmation } from '../modal-confirmation';

import './style.scss';

export interface ModalShareProps {
  visible: boolean;
  isShare: boolean;
  type: DownLoadShareType;
  convertData: (members: any, comment: string) => any;
  onCancel: () => void;
  onLoading: (loading: boolean) => void;
}

export function ModalShare({
  visible,
  isShare,
  type,
  convertData,
  onCancel: onCancelProp,
  onLoading,
}: ModalShareProps) {
  const intl = useIntl();
  const { account } = useSelector((state: FunctionalRootState) => state.loginStore);

  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isDownload, setIsDownload] = useState(false);
  const [comment, setComment] = useState('');
  const [errorMessages, setErrorMessages] = useState<string[] | undefined>(undefined);
  const [members, setMembers] = useState<string[]>([]);

  const resetFormState = useCallback(() => {
    setComment('');
    setMembers([]);
    setErrorMessages(undefined);
  }, []);

  const handleDownload = useCallback(
    (skip: boolean, membersArg?: string[]) => {
      const m = membersArg ?? members;
      const prevLoading = loading;
      setLoading(true);
      onLoading(true);
      HandleDownload(
        type,
        prevLoading && !skip,
        {
          ...convertData(m, comment),
        },
        intl,
        () => {
          setLoading(false);
          onLoading(false);
          onCancelProp();
        },
        () => {
          resetFormState();
        },
        (messages: string[] | undefined) => {
          const errs = messages?.map((msg) => `- ${msg}`);
          setShowConfirmModal(true);
          setIsDownload(true);
          setErrorMessages(errs);
        },
      );
    },
    [loading, members, comment, type, convertData, intl, onLoading, onCancelProp, resetFormState],
  );

  const handleShare = useCallback(
    (skip: boolean, membersArg?: string[]) => {
      const m = membersArg ?? members;
      const prevLoading = loading;
      setLoading(true);
      onLoading(true);
      HandleShare(
        type,
        prevLoading && !skip,
        {
          ...convertData(m, comment),
          continue: skip,
        },
        intl,
        () => {
          setLoading(false);
          onLoading(false);
          onCancelProp();
        },
        () => {
          resetFormState();
        },
        (messages: string[] | undefined) => {
          const errs = messages?.map((msg) => `- ${msg}`);
          setShowConfirmModal(true);
          setIsDownload(false);
          setErrorMessages(errs);
        },
      );
    },
    [loading, members, comment, type, convertData, intl, onLoading, onCancelProp, resetFormState],
  );

  const onFinish = (values: { names: string[] }) => {
    const names = values.names;
    setMembers(names);
    handleShare(false, names);
  };

  const onConfirm = () => {
    const wasDownload = isDownload;
    setShowConfirmModal(false);
    setIsDownload(false);
    if (wasDownload) {
      handleDownload(true);
    } else {
      handleShare(true);
    }
  };

  const onConfirmationCancel = () => {
    setShowConfirmModal(false);
    setIsDownload(false);
    setComment('');
    setMembers([]);
    setLoading(false);
    setErrorMessages(undefined);
    onLoading(false);
    onCancelProp();
  };

  const onChangeComment = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);
  };

  const onModalCancel = () => {
    setComment('');
    setErrorMessages(undefined);
    onCancelProp();
  };

  const { TextArea } = Input;

  const formItemLayoutWithOutLabel = {
    wrapperCol: {
      xs: { span: 24, offset: 0 },
      sm: { span: 20, offset: 4 },
    },
  };

  const fontFamily = account?.fontFamily;
  const secondaryFontFamily = account?.secondaryFontFamily;

  return (
    <Modal
      className={`modal-share ${fontFamily ? `${fontFamily}-font` : ''} ${secondaryFontFamily ? `secondary-${secondaryFontFamily}-font` : ''}`}
      open={visible}
      centered
      footer={null}
      onCancel={onModalCancel}
      wrapClassName="modal-share__wrapper"
      width={500}
    >
      <div>
        {isShare ? (
          <>
            <h4 className="modal-share__title secondary-font">
              <FormattedMessage id="enter.member.id.or.email" />
            </h4>
            <div className="modal-share__hotels">
              <Form
                name="dynamic_form_item"
                style={{ width: '100%' }}
                {...formItemLayoutWithOutLabel}
                onFinish={onFinish}
              >
                <Form.List
                  name="names"
                  rules={[
                    {
                      validator: async (_, names) => {
                        if (!names || names.length < 1) {
                          return Promise.reject(new Error('At least 1 recipient'));
                        }
                      },
                    },
                  ]}
                >
                  {(fields, { add, remove }, { errors }) => (
                    <>
                      {fields.map((field) => (
                        <Form.Item {...formItemLayoutWithOutLabel} required={false} key={field.key}>
                          <Form.Item
                            {...field}
                            validateTrigger={['onChange', 'onBlur']}
                            rules={[
                              {
                                required: true,
                                whitespace: true,
                                message: <FormattedMessage id="please.input.member.id.or.email.or.delete" />,
                              },
                            ]}
                            noStyle
                          >
                            <Input
                              placeholder={intl.formatMessage({ id: 'please.input.member.id.or.email' })}
                              style={{ width: '90%' }}
                            />
                          </Form.Item>
                          {fields.length > 1 ? (
                            <MinusCircleOutlined
                              className="dynamic-delete-button"
                              onClick={() => remove(field.name)}
                            />
                          ) : null}
                        </Form.Item>
                      ))}
                      <Form.Item>
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          style={{ width: '90%' }}
                          icon={<PlusOutlined />}
                        >
                          <FormattedMessage id="add.another.member.id.or.email" />
                        </Button>
                        <Form.ErrorList errors={errors} />
                      </Form.Item>
                    </>
                  )}
                </Form.List>
                <h4 className="modal-share__title secondary-font">
                  <FormattedMessage id="results.hotel.card.compare.modal.comment.title" />
                </h4>
                <div className="modal-share__hotels">
                  <TextArea rows={4} value={comment || ''} onChange={onChangeComment} />
                </div>

                <div className="modal-share__buttons">
                  <BlueButton disabled={loading} htmlType="submit">
                    {loading ? <Spin /> : <FormattedMessage id="results.hotel.card.compare.modal.share" />}
                  </BlueButton>
                </div>
              </Form>
            </div>
          </>
        ) : null}

        {!isShare ? (
          <>
            <h4 className="modal-share__title secondary-font">
              <FormattedMessage id="results.hotel.card.compare.modal.comment.title" />
            </h4>
            <div className="modal-share__hotels">
              <TextArea rows={4} value={comment || ''} onChange={onChangeComment} />
            </div>

            <div className="modal-share__buttons">
              <BlueButton disabled={loading} onClick={() => handleDownload(false)}>
                {loading ? <Spin /> : <FormattedMessage id="results.hotel.card.compare.modal.download" />}
              </BlueButton>
            </div>
          </>
        ) : null}
      </div>

      <ModalConfirmation
        wrapClassName="modal-share__confirmation-wrapper"
        title="share.avail.title"
        message={'share.avail.message'}
        innerMessages={errorMessages}
        messageAux="share.avail.message.aux"
        auxMessageColor="red"
        yesMessage="share.continue"
        noMessage="share.cancel"
        visible={showConfirmModal}
        onCancel={onConfirmationCancel}
        onConfirm={onConfirm}
      />
    </Modal>
  );
}
